import { t } from "i18next";
import * as Yup from "yup";

import type { TSection } from "@/types";

/**
 * The "Invoicing" tab of one of the user's companies: what goes on its
 * invoices besides its identity. A function for the same reason as
 * COMPANY_DETAILS_SECTIONS: `t()` must run after i18next.init().
 *
 * The wording of the terms and mentions is the user's own; nothing here
 * knows which mentions a given legal status requires.
 */
export const COMPANY_BILLING_SECTIONS = (): TSection[] => [
	{
		key: "bank",
		title: t("company.billing.groups.bank"),
		validationSchema: Yup.object().shape({
			iban: Yup.string()
				.transform((value: string) => value?.replace(/\s+/g, "") ?? "")
				.matches(/^[A-Za-z0-9]{0,34}$/, t("forms.invalid")),
			bic: Yup.string().matches(
				/^[A-Za-z0-9]{0,11}$/,
				t("forms.invalid")
			),
		}),
		fields: [
			{ name: "iban", type: "input", label: t("company.billing.iban") },
			{ name: "bic", type: "input", label: t("company.billing.bic") },
		],
	},
	{
		key: "numbering",
		title: t("company.billing.groups.numbering"),
		validationSchema: Yup.object().shape({
			invoicePrefix: Yup.string().matches(
				/^[A-Za-z0-9._/]{0,20}$/,
				t("forms.invalid")
			),
		}),
		fields: [
			{
				name: "invoicePrefix",
				type: "input",
				label: t("company.billing.invoicePrefix"),
				displayProps: {
					info: { text: t("company.billing.invoicePrefixHint") },
				},
			},
			{
				name: "defaultCurrency",
				type: "input",
				label: t("company.billing.defaultCurrency"),
			},
		],
	},
	{
		key: "vat",
		title: t("company.billing.groups.vat"),
		validationSchema: Yup.object().shape({
			defaultVatRate: Yup.number()
				.nullable()
				.min(0, t("forms.invalid"))
				.max(100, t("forms.invalid")),
		}),
		fields: [
			{
				name: "vatApplicable",
				type: "toggle",
				label: t("company.billing.vatApplicable"),
				valueDisplay: (values) =>
					(values as { vatApplicable?: boolean }).vatApplicable
						? t("actions.yes")
						: t("actions.no"),
			},
			{
				name: "defaultVatRate",
				type: "number",
				label: t("company.billing.defaultVatRate"),
				min: 0,
				max: 100,
				after: "%",
			},
		],
	},
	{
		key: "terms",
		title: t("company.billing.groups.terms"),
		validationSchema: Yup.object().shape({
			paymentTermsDays: Yup.number()
				.nullable()
				.min(0, t("forms.invalid"))
				.max(365, t("forms.invalid")),
		}),
		fields: [
			{
				name: "paymentTermsDays",
				type: "number",
				label: t("company.billing.paymentTermsDays"),
				min: 0,
				max: 365,
			},
			{
				name: "paymentTerms",
				type: "textarea",
				label: t("company.billing.paymentTerms"),
				rows: 4,
				displayProps: {
					info: { text: t("company.billing.paymentTermsHint") },
				},
			},
		],
	},
	{
		key: "mentions",
		title: t("company.billing.groups.mentions"),
		fields: [
			{
				name: "legalMentions",
				type: "textarea",
				label: t("company.billing.legalMentions"),
				rows: 4,
				displayProps: {
					info: { text: t("company.billing.legalMentionsHint") },
				},
			},
		],
	},
];
